import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { IconChevron } from '../icons/WizardIcons';
import { alSelClass } from '../../utils/alSelClass';

export type LookupFieldSelectOption = {
  id: string;
  label: string;
};

export type LookupFieldSelectProps = {
  id?: string;
  label: string;
  required?: boolean;
  value: string;
  placeholder?: string;
  options: LookupFieldSelectOption[];
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  clearable?: boolean;
  emptyMessage?: string;
  className?: string;
  /** When true, user can type to filter dropdown options by label. */
  searchable?: boolean;
  /** Caps typed search text when `searchable` is true. */
  maxLength?: number;
};

function filterOptionsByQuery(
  options: LookupFieldSelectOption[],
  query: string,
): LookupFieldSelectOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.label.toLowerCase().includes(q));
}

export function LookupFieldSelect({
  id,
  label,
  required,
  value,
  placeholder,
  options,
  onChange,
  error,
  disabled,
  clearable = true,
  emptyMessage = 'No data found',
  className,
  searchable = false,
  maxLength,
}: LookupFieldSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const selectDisabled = disabled || options.length === 0;
  const isSearchable = searchable && !selectDisabled;
  const showClear = clearable && Boolean(value) && !selectDisabled;
  const showNoData = !selectDisabled && options.length === 0;
  const selectedLabel = options.find((o) => o.id === value)?.label ?? '';
  const displayText = value ? selectedLabel : placeholder ?? 'Select';
  const searchDisplayValue = open ? query : value ? selectedLabel : '';
  /** Placeholder tone only when nothing is shown; typed/selected text uses normal color. */
  const triggerSelClass =
    isSearchable
      ? searchDisplayValue
        ? 'al-sel'
        : 'al-sel al-sel-ph'
      : alSelClass(value);

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !open) return options;
    return filterOptionsByQuery(options, query);
  }, [isSearchable, open, options, query]);

  const listOptions = isSearchable ? filteredOptions : options;
  const showNoMatches = isSearchable && open && !showNoData && listOptions.length === 0;

  useEffect(() => {
    if (!isSearchable || open) return;
    setQuery(selectedLabel);
  }, [isSearchable, open, selectedLabel]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (selectDisabled) setOpen(false);
  }, [selectDisabled]);

  const openSearchable = () => {
    if (selectDisabled) return;
    setOpen(true);
    setQuery(selectedLabel);
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  return (
    <div className={['al-f', className].filter(Boolean).join(' ')}>
      <div className="al-l">
        {label} {required ? <span className="req">*</span> : null}
      </div>
      <div
        ref={wrapRef}
        className={`al-sel-wrap al-lookup-sel-wrap${showClear ? ' has-clear' : ''}${open ? ' is-open' : ''}${selectDisabled ? ' is-disabled' : ''}${isSearchable ? ' is-searchable' : ''}`}
      >
        {isSearchable ? (
          <input
            ref={inputRef}
            id={id}
            type="text"
            className={`al-lookup-sel-trigger al-lookup-sel-search ${triggerSelClass}`}
            value={searchDisplayValue}
            placeholder={placeholder ?? 'Select'}
            maxLength={maxLength}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            aria-invalid={error ? true : undefined}
            style={error ? { borderColor: 'var(--red)' } : undefined}
            onFocus={openSearchable}
            onClick={openSearchable}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
          />
        ) : (
          <button
            type="button"
            id={id}
            className={`al-lookup-sel-trigger ${triggerSelClass}`}
            disabled={selectDisabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            aria-invalid={error ? true : undefined}
            style={error ? { borderColor: 'var(--red)' } : undefined}
            onClick={() => {
              if (selectDisabled) return;
              setOpen((v) => !v);
            }}
          >
            <span className="al-lookup-sel-value">{displayText}</span>
          </button>
        )}
        {open && !selectDisabled ? (
          <ul id={listId} className="al-lookup-sel-menu" role="listbox">
            {showNoData || showNoMatches ? (
              <li>
                <span className="al-lookup-sel-empty">{emptyMessage}</span>
              </li>
            ) : (
              listOptions.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={o.id === value}
                    className={o.id === value ? 'is-selected' : undefined}
                    onClick={() => {
                      onChange(o.id);
                      setQuery(o.label);
                      setOpen(false);
                    }}
                  >
                    {o.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        ) : null}
        {showClear ? (
          <button
            type="button"
            className="al-sel-clear"
            aria-label={`Clear ${label}`}
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              setQuery('');
              setOpen(false);
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        ) : null}
        <span className="al-lookup-sel-chev" aria-hidden>
          <IconChevron expanded={open} />
        </span>
      </div>
      {error ? (
        <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
